import {useCallback, useEffect, useState} from 'react';

import {getUserSeatAssignments} from 'src/openapi/admin/apis/UserSeatAssignmentsApi';
import {useAppDispatch} from 'src/store/hooks';

import type {Error as ApiError} from 'src/openapi/admin/models/Error';
import type {SeatAssignmentStatus} from 'src/openapi/admin/models/SeatAssignmentStatus';
import type {UserSeatAssignment} from 'src/openapi/admin/models/UserSeatAssignment';

const useSeatAssignments = (
    userId: number,
    status: SeatAssignmentStatus | undefined,
    {page, size, loadAll}: {page: number, size: number, loadAll: boolean},
) => {
    const apiDispatch = useAppDispatch();
    const [assignments, setAssignments] = useState<UserSeatAssignment[] | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [error, setError] = useState<ApiError | null>(null);
    const [refreshVersion, setRefreshVersion] = useState(0);

    useEffect(() => {
        const abortController = new AbortController();
        setAssignments(null);
        setTotalCount(0);
        setError(null);

        const loadAssignments = async () => {
            try {
                const loadedAssignments: UserSeatAssignment[] = [];
                let nextPage = page;
                let count = 0;
                do {
                    const response = await apiDispatch(getUserSeatAssignments(
                        {userId, status, page: nextPage, size}, {signal: abortController.signal},
                    ));
                    if (abortController.signal.aborted) {
                        return;
                    }
                    if (!response.payload?.success) {
                        throw response.payload?.data;
                    }
                    const items = response.payload.data?.content || [];
                    count = response.payload.data?.totalCount || 0;
                    if (loadAll && !items.length && loadedAssignments.length < count) {
                        throw new Error('Unable to load all seat assignments. Please try again.');
                    }
                    loadedAssignments.push(...items);
                    nextPage += 1;
                } while (loadAll && loadedAssignments.length < count);

                setAssignments(loadedAssignments);
                setTotalCount(count);
            } catch (requestError) {
                if (!abortController.signal.aborted) {
                    setError((requestError as ApiError) || {message: 'Unable to load seat assignments.'});
                }
            }
        };

        loadAssignments();
        return () => abortController.abort();
    }, [apiDispatch, loadAll, page, refreshVersion, size, status, userId]);

    const refresh = useCallback(() => setRefreshVersion(version => version + 1), []);

    return {assignments, totalCount, error, refresh};
};

export const useUserSeatAssignments = (userId: number, status?: SeatAssignmentStatus, page = 1, size = 25) =>
    useSeatAssignments(userId, status, {page, size, loadAll: false});

// PUMA exposes pagination only; local search and sorting need the complete collection.
export const useAllUserSeatAssignments = (userId: number, status?: SeatAssignmentStatus) =>
    useSeatAssignments(userId, status, {page: 1, size: 250, loadAll: true});
